import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, lastValueFrom } from 'rxjs';
import { finalize, map, shareReplay, tap } from 'rxjs/operators';
import { Form } from '../models/application/form';
import { BaseService } from './base.service';
import { FormType } from '../models/application/formType';
import { FormSharedWithWorkspaceModel } from '../models/form/form-shared-with-workspace.model';
import { ApiVersions } from './api-version';

@Injectable({ providedIn: 'root' })
export class FormService extends BaseService {
  private basePath: string = 'forms';
  private _pendingGetAll: Observable<Form[]>;
  private _form: BehaviorSubject<Form> = new BehaviorSubject<Form>(null);
  private _forms: BehaviorSubject<Form[]> = new BehaviorSubject(new Array<Form>());
  private _activeTemplates: BehaviorSubject<Form[]> = new BehaviorSubject(null);
  public readonly form: Observable<Form> = this._form.asObservable();
  public readonly forms: Observable<Form[]> = this._forms.asObservable();
  public readonly activeTemplates: Observable<Form[]> = this._activeTemplates.asObservable();

  constructor() {
    super();
  }

  getFormWithoutQuestionsById(id: string) {
    let path = 'forms/light-version'.concat('/', id);
    return this.get(path, true).pipe(map((data) => Form.parse(data)));
  }
  getById(id: string) {
    let path = 'forms'.concat('/', id);
    let obs = this.get(path).pipe(map((data) => Form.parse(data)));
    obs.subscribe((result) => {
      this._form.next(result);
      let forms = this._forms.getValue();
      let index = forms.findIndex((a) => a.id == result.id);
      forms[index] = result;
      this._forms.next(forms);
    });
  }

  setNext(form: Form) {
    let forms = this._forms.getValue();
    let index = forms.findIndex((a) => a.id == form.id);
    if (index == -1) {
      forms.push(form);
    } else {
      forms[index] = form;
    }
    this._form.next(form);
    this._forms.next(forms);
  }

  getAll(): Observable<Form[]> {
    if (!this._pendingGetAll) {
      this._pendingGetAll = this.get(this.basePath).pipe(
        map((data) => (data ?? []).map((app) => Form.parse(app))),
        tap((data) => this._forms.next(data)),
        finalize(() => (this._pendingGetAll = null)),
        shareReplay(1)
      );
      this._pendingGetAll.subscribe();
    }
    return this._pendingGetAll;
  }

  getAllActiveTemplates(formType?: FormType) {
    let path = `${this.basePath}/active-templates/${formType}`;
    let obs = this.get(path).pipe(map((data) => data.map((app) => Form.parse(app))));
    obs.subscribe((data) => {
      this._activeTemplates.next(data);
    });
  }

  getFormWorkspaces = async (formId: string): Promise<FormSharedWithWorkspaceModel[]> => {
    return await lastValueFrom(this.get(`form-workspaces/${formId}`, null, ApiVersions.v0_1));
  };
}
